import { chunk, uniq } from "lodash";

import { AppStateProvider } from "services/app-state-provider";
import { selectAgentName } from "store/entities/agents";
import { selectTeamName } from "store/entities/teams";
import {
  hasReportAgentFilterComparison,
  hasReportComparisonDisabled,
  hasReportGroupFilterComparison,
} from "store/views/reports";

import "./chart.css";
import { getFormattedNumberValue } from "./string";
import { formatTimeFromHours } from "./time";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defaultHeaderFormatter({ label, filters = null }: any): string {
  const labelParts = [];
  const state = AppStateProvider.getState();

  // format for time range for hours distribution
  if (/^[0-9]+:00 (am|pm)/g.test(label)) {
    const hour = parseInt(label, 10);
    const hourAfter = hour + 1 + (label.includes("pm") ? 12 : 0) + (label === "12:00 am" ? -12 : 0);
    labelParts.push(`${label} - ${formatTimeFromHours((hourAfter >= 0 ? hourAfter : 23).toString())}`);
  } else {
    labelParts.push(label);
  }

  if (filters && !hasReportComparisonDisabled(state)) {
    if (filters.group && hasReportGroupFilterComparison(state)) {
      const teamName = selectTeamName(state, filters.group);
      labelParts.push(`<div class="chartts-label" alt="${teamName}">${teamName}</div>`);
    }

    if (filters.agent && hasReportAgentFilterComparison(state)) {
      const agentName = selectAgentName(state, filters.agent);
      labelParts.push(`<div class="chartts-label" alt="${agentName}">${agentName}</div>`);
    }
  }

  return labelParts.join("");
}

export function getColorPointer(color: string): string {
  return `<div class="chartts-label" style="background-color: ${color};"></div>`;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getExtraLabel(point: any): string {
  const { index } = point;
  const label = point.series.options.label && point.series.options.label[index];

  return label ? `<span class="chartts-extra-label">${label}</span>` : "";
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defaultPointFormatter(point: any): string {
  return `
    ${point.series.chart.series.length > 1 ? getColorPointer(point.series.color) : ""}
    ${getFormattedNumberValue(point.y)}
    ${point.series.name}
    ${getExtraLabel(point)}
  `;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defaultBodyFormatter(context: ITooltipSeries, pointFormatter: any): string {
  if (context.point) {
    return pointFormatter(context.point);
  }
  if (context.points) {
    return context.points.map(pointFormatter).join("<br/>");
  }
  return "";
}

interface ITooltipSeries {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  x: any;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y: any;
  label: string;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  series: any;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters?: any;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  point?: any;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  points?: any;
}

/*
 * Extract point context to formatter payload
 */
export // FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function serializeTooltipData({ series, point, points, x, y }: any, forceSplitTooltip: boolean): ITooltipSeries[] {
  const labels = x.label.split("<br>");

  // Get point if tooltip show single series data
  if (point) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const label = labels[point.series.chart.series.map((s: any) => s.color).indexOf(point.color) % x.values.length];
    const filters = point ? point.series.userOptions.filters : null;
    return [
      {
        x,
        y,
        label,
        series,
        point,
        filters,
      },
    ];
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pointsPerSeries = uniq(points.map((p: any) => p.series.name)).length;
  const seriesCount = forceSplitTooltip ? pointsPerSeries : points.length / pointsPerSeries;

  return (
    Array(seriesCount)
      .fill(null)
      // @ts-expect-error TS(6133): 'el' is declared but its value is never read.
      .map((el, seriesIndex) => {
        const seriesPoints = points
          ? // @ts-expect-error TS(6133): 'p' is declared but its value is never read.
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            points.filter((p: any, index: any) => index % seriesCount === seriesCount - 1 - seriesIndex)
          : null;
        const filters = seriesPoints && seriesPoints.length ? seriesPoints[0].series.userOptions.filters : null;
        const label = labels[(seriesCount - seriesIndex - 1) % labels.length];
        return {
          x,
          y,
          label,
          series,
          points: seriesPoints,
          filters,
        };
      })
      .reverse()
  );
}

/*
 * Split data point for series and represent them on separated blocks
 */
export const getChartFormatter = (
  pointFormatter = defaultPointFormatter,
  forceSplitTooltip = false // FIXME: Fix this the next time the file is edited.
) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (this: any): string {
    const series = serializeTooltipData(this, forceSplitTooltip).map(
      context => `
      <div class="tooltip-header chartts-chart-header">${defaultHeaderFormatter(context)}</div>
      <div class="chartts-chart-body">${defaultBodyFormatter(context, pointFormatter)}</div>
    `
    );

    return `
    <div class="chartts-container lc-chart-tooltip" style="--chevron-left: 50%">
      <table>
      ${(chunk(series, 2) as [][]).map(group => `<tr>${group.map(s => `<td>${s}</td>`).join("")}</tr>`).join("")}
      </table>
    </div>
  `;
  };
